import React, { useEffect, useMemo, useState } from 'react';
import Modal from 'src/modules/shared/components/molecules/modals/modal';
import { ModalFooter } from 'src/modules/shared/components/molecules/modals/modal-footer';
import { useCreateUser } from 'src/modules/shared/hooks/api/admin/users.mutation';
import { ApiError } from 'src/api/generated';
import { isValidEmail } from 'src/modules/shared/utils/validation/email.util';
import { useAlert } from 'src/contexts/alert-context';
import { extractApiErrorMessage } from 'src/modules/shared/utils/api/error-handler.util';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import LabelTextInput from 'src/modules/shared/components/atoms/inputs/label-text-input';
import LabelSelect from 'src/modules/shared/components/atoms/inputs/label-select';
import { Text } from 'src/modules/shared/components/atoms/text';
import { isValidPhoneNumber } from 'src/modules/shared/utils/validation/phone.util';

interface Team {
  id: number;
  name: string | null;
}

interface InviteUserModalProps {
  isOpen: boolean;
  teams: Team[];
  onSuccess: () => void;
  onClose: () => void;
}

export const InviteUserModal: React.FC<InviteUserModalProps> = ({
  isOpen,
  teams,
  onSuccess,
  onClose,
}) => {
  const { showAlert } = useAlert();
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [individualPhoneNumber, setIndividualPhoneNumber] = useState('');
  const [profile, setProfile] = useState<'agent' | 'admin'>('agent');
  const [teamId, setTeamId] = useState('');

  const getCreateUserErrorMessage = (error: Error) => {
    if (error instanceof ApiError && [400, 409].includes(error.status)) {
      return t('users.invite.userAlreadyExists');
    }

    return extractApiErrorMessage(error);
  };

  const { createUser, loading } = useCreateUser(
    () => {
      showAlert(t('users.invite.success'), 'success');
      onSuccess();
    },
    (error) => {
      showAlert(getCreateUserErrorMessage(error), 'error');
    }
  );

  useEffect(() => {
    if (isOpen) {
      return;
    }

    setEmail('');
    setFirstName('');
    setLastName('');
    setIndividualPhoneNumber('');
    setProfile('agent');
    setTeamId('');
  }, [isOpen]);

  const profileOptions = useMemo(
    () => [
      { content: t('users.profile.agent'), value: 'agent' },
      { content: t('users.profile.admin'), value: 'admin' },
    ],
    [t]
  );

  const teamOptions = useMemo(
    () => [
      { content: t('users.invite.team.none'), value: '' },
      ...teams.map((team) => ({
        content: team.name ?? t('users.team.fallback', { id: team.id }),
        value: String(team.id),
      })),
    ],
    [teams, t]
  );

  const trimmedFirstName = firstName.trim();
  const trimmedLastName = lastName.trim();
  const trimmedIndividualPhoneNumber = individualPhoneNumber.trim();
  const isIndividualPhoneNumberValid =
    trimmedIndividualPhoneNumber.length === 0 ||
    isValidPhoneNumber(trimmedIndividualPhoneNumber);
  const individualPhoneNumberError =
    trimmedIndividualPhoneNumber.length > 0 && !isIndividualPhoneNumberValid
      ? t('users.table.phoneInvalid')
      : undefined;
  const canSubmit =
    isValidEmail(email) &&
    trimmedFirstName.length > 0 &&
    trimmedLastName.length > 0 &&
    isIndividualPhoneNumberValid;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    await createUser({
      email,
      firstName: trimmedFirstName,
      lastName: trimmedLastName,
      ...(trimmedIndividualPhoneNumber
        ? { individualPhoneNumber: trimmedIndividualPhoneNumber }
        : {}),
      profile,
      teamId: teamId ? Number(teamId) : null,
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      dialogClassName="sm:max-w-xl"
      header={
        <Text as="h3" size="h3" weight="medium">
          {t('users.invite.modal.title')}
        </Text>
      }
      body={
        <div className="flex w-full flex-col gap-4 py-2">
          <Text size="small" color="neutral">
            {t('users.invite.modal.description')}
          </Text>
          <LabelTextInput
            label={t('users.invite.email')}
            labelPosition="top"
            inputProps={{
              type: 'email',
              value: email,
              onChange: (event) => setEmail(event.target.value),
              placeholder: t('users.invite.emailPlaceholder'),
              error:
                !isValidEmail(email) && email.length > 0
                  ? t('users.invite.emailInvalid')
                  : undefined,
            }}
          />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <LabelTextInput
              label={t('users.invite.firstName')}
              labelProps={{ required: true }}
              labelPosition="top"
              inputProps={{
                required: true,
                value: firstName,
                onChange: (event) => setFirstName(event.target.value),
                placeholder: t('users.invite.firstNamePlaceholder'),
              }}
            />
            <LabelTextInput
              label={t('users.invite.lastName')}
              labelProps={{ required: true }}
              labelPosition="top"
              inputProps={{
                required: true,
                value: lastName,
                onChange: (event) => setLastName(event.target.value),
                placeholder: t('users.invite.lastNamePlaceholder'),
              }}
            />
          </div>
          <div className="flex flex-col gap-1">
            <LabelTextInput
              label={t('users.invite.personalPhone')}
              labelPosition="top"
              inputProps={{
                type: 'tel',
                value: individualPhoneNumber,
                onChange: (event) =>
                  setIndividualPhoneNumber(event.target.value),
                placeholder: t('users.invite.personalPhonePlaceholder'),
                error: individualPhoneNumberError,
              }}
            />
            <Text size="tiny" color="secondary" className="leading-4">
              {t('users.invite.personalPhoneHelp')}
            </Text>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <LabelSelect
              label={t('users.invite.profile')}
              labelPosition="top"
              options={profileOptions}
              selectProps={{
                value: profile,
                onChange: (event) =>
                  setProfile(
                    event.target.value === 'admin' ? 'admin' : 'agent'
                  ),
              }}
            />
            <LabelSelect
              label={t('users.invite.team')}
              labelPosition="top"
              options={teamOptions}
              selectProps={{
                value: teamId,
                onChange: (event) => setTeamId(event.target.value),
              }}
            />
          </div>
        </div>
      }
      footer={
        <ModalFooter
          onCancel={onClose}
          onConfirm={handleSubmit}
          cancelText={t('users.invite.modal.cancel')}
          confirmText={t('users.actions.invite')}
          confirmDisabled={!canSubmit || loading}
          loading={loading}
        />
      }
    />
  );
};
